import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

/**
 * Post-processing. Bloom is the single biggest fidelity-per-line win in a stylised 3D scene: it is
 * what makes emissive collectibles read as light rather than as bright paint.
 *
 * Two rules it follows:
 *  - THRESHOLD, don't blanket. A low threshold blooms the whole image into haze. Only pixels above
 *    the threshold glow, which is why the orbs and the player glow while the track does not.
 *  - It must be DISPOSABLE at runtime. The quality governor turns post-processing off on a struggling
 *    device, so this returns a handle that can be torn down and rebuilt without recreating the scene.
 *
 * OutputPass is mandatory when compositing: it applies tone mapping and the output colour space that
 * the renderer would normally apply itself. Without it every colour comes out washed and flat.
 */
export interface PostFx {
	composer: EffectComposer;
	setSize: (width: number, height: number, pixelRatio: number) => void;
	dispose: () => void;
}

export function createPostFx(
	renderer: THREE.WebGLRenderer,
	scene: THREE.Scene,
	camera: THREE.Camera,
	width: number,
	height: number,
): PostFx {
	const composer = new EffectComposer(renderer);
	composer.addPass(new RenderPass(scene, camera));

	const bloom = new UnrealBloomPass(
		new THREE.Vector2(width, height),
		0.85, // strength — enough to feel lit, short of a smear
		0.5, // radius
		0.72, // threshold: only genuinely bright pixels bloom
	);
	composer.addPass(bloom);
	composer.addPass(new OutputPass());

	return {
		composer,
		setSize: (w, h, pixelRatio) => {
			composer.setSize(w, h);
			composer.setPixelRatio(pixelRatio);
			bloom.setSize(w, h);
		},
		dispose: () => {
			bloom.dispose();
			composer.dispose();
		},
	};
}
